import {
    FileOutlined,
    FolderOutlined,
    MoreOutlined,
    PlusOutlined,
    UploadOutlined,
    CopyOutlined,
    ScissorOutlined,
    SnippetsOutlined
} from '@ant-design/icons';

import {
    Button,
    Card,
    Checkbox,
    Col,
    Dropdown,
    Input,
    Menu,
    message,
    Modal,
    Row,
    Table,
    Typography,
    Upload,
    Breadcrumb,
    Spin
} from "antd";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    createFolder,
    deleteFolder,
    fetchFolderList,
    getFolderFiles,
    moveFile,
    moveFolder,
    renameFolder,
    setSliceCurrentFolderId,
    uploadFile,
    copyFolder,
    copyFile
} from "../../redux/rtk/features/document/documentSlice";

import { assignFolderPermission } from '../../redux/rtk/features/document/documentSlice';
import { loadAllStaff } from '../../redux/rtk/features/user/userSlice';
import getUserFromToken from "../../utils/getUserFromToken";

const { Title } = Typography;

const FolderManager = () => {
    const [newFolderName, setNewFolderName] = useState("");
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [renameFolderName, setRenameFolderName] = useState("");
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selectedFolderId, setSelectedFolderId] = useState(null);
    const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
    const [isUserListModalVisible, setIsUserListModalVisible] = useState(false);
    const [selectedEditUsers, setSelectedEditUsers] = useState([]);
    const [selectedViewUsers, setSelectedViewUsers] = useState([]);
    const [selectedFolderName, setSelectedFolderName] = useState([]);
    const [currentFolderId, setCurrentFolderId] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [folderSearchQuery, setFolderSearchQuery] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);

    // Clipboard state for cut/copy/paste functionality
    const [clipboard, setClipboard] = useState({
        item: null,
        operation: null, // 'cut' or 'copy'
        sourceFolder: null
    });

    // Drag and drop state
    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverItem, setDragOverItem] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const dropZoneRef = useRef(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userId = getUserFromToken();

    const folders = useSelector((state) => state.document?.folders || []);
    const folderContent = useSelector((state) => state.document?.folderContent || { folders: [], files: [] });
    // Only show spinner for folder/file operations (staff list loads separately)
    const documentLoading = useSelector((state) => state.document?.folderLoading || false);
    const userList = useSelector((state) => state.users?.list || []);
    const [breadcrumbItems, setBreadcrumbItems] = useState([{ title: 'Home', id: 0 }]);
    const cfid = useSelector((state) => state.document.currentFolderId);


    useEffect(() => {
        if (cfid) setCurrentFolderId(cfid);

        // Folder data should start loading immediately; staff list can load in background.
        dispatch(fetchFolderList(userId));
        dispatch(getFolderFiles(cfid || 0));
        dispatch(loadAllStaff({ status: true }));
    }, [dispatch, userId, cfid]);

    useEffect(() => {
        if (folderContent?.folders && selectedFolderId) {
            const selectedFolder = folderContent.folders.find(folder => folder.id === selectedFolderId);
            if (selectedFolder) {
                setSelectedViewUsers(selectedFolder.userId || []);
                setSelectedEditUsers(selectedFolder.editFolderUserId || []);
            }
        }
    }, [folderContent, selectedFolderId]);

    // Function to build breadcrumb path from current folder to root
    const buildBreadcrumb = (folderId) => {
        const breadcrumb = [{ title: 'Home', id: 0 }];
        
        if (!folderId || folderId === 0) {
            setBreadcrumbItems(breadcrumb);
            return;
        }

        const pathItems = [];
        
        const buildPath = (currentId) => {
            // Try to find folder in folders array first
            let folder = folders.find(f => f.folder?.id === currentId);
            
            // If not found, try to find in folderContent.folders
            if (!folder) {
                const folderFromContent = folderContent.folders.find(f => f.id === currentId);
                if (folderFromContent) {
                    folder = { folder: { 
                        id: folderFromContent.id, 
                        folderName: folderFromContent.folderName,
                        parentId: folderFromContent.parentId 
                    }};
                }
            }
            
            if (folder && folder.folder) {
                // Add current folder to path
                pathItems.push({
                    title: folder.folder.folderName,
                    id: folder.folder.id
                });

                // If folder has a parent, continue building path
                if (folder.folder.parentId && folder.folder.parentId !== 0) {
                    buildPath(folder.folder.parentId);
                }
            }
        };

        buildPath(folderId);
        
        // Reverse pathItems to get correct order (root to current)
        pathItems.reverse();
        
        // Combine Home with path items
        setBreadcrumbItems([...breadcrumb, ...pathItems]);
    };

    // Update breadcrumb when currentFolderId, folders, or folderContent change
    useEffect(() => {
        buildBreadcrumb(currentFolderId);
    }, [currentFolderId, folders, folderContent]);

    // Handle breadcrumb navigation
    const handleBreadcrumbClick = async (folderId) => {
        try {
            dispatch(setSliceCurrentFolderId(folderId));
            setCurrentFolderId(folderId);
            await dispatch(getFolderFiles(folderId || 0)).unwrap();
        } catch (error) {
            console.error('Error navigating to folder:', error);
            message.error('Failed to navigate to folder');
        }
    };

    // Cut functionality
    const handleCut = (item) => {
        setClipboard({
            item: item,
            operation: 'cut',
            sourceFolder: currentFolderId
        });
        message.success(`${item.type === 'folder' ? 'Folder' : 'File'} "${item.folderName}" cut to clipboard`);
    };

    // Copy functionality
    const handleCopy = (item) => {
        setClipboard({
            item: item,
            operation: 'copy',
            sourceFolder: currentFolderId
        });
        message.success(`${item.type === 'folder' ? 'Folder' : 'File'} "${item.folderName}" copied to clipboard`);
    };

    // Paste functionality
    const handlePaste = async () => {
        if (!clipboard.item) {
            message.warning("No item in clipboard");
            return;
        }

        try {
            const { item, operation, sourceFolder } = clipboard;

            if (operation === 'cut') {
                // Move the item to current folder
                if (item.type === 'folder') {
                    await handleMoveFolder(item.id, currentFolderId, item.folderName);
                } else {
                    await handleMoveFile(item.id, currentFolderId);
                }
                message.success(`${item.type === 'folder' ? 'Folder' : 'File'} moved successfully`);
                
                // Clear clipboard after cut operation
                setClipboard({ item: null, operation: null, sourceFolder: null });
            } else if (operation === 'copy') {
                // For copy operation, you'll need to implement duplicate functionality
                // This depends on your backend API
                if (item.type === 'folder') {
                    await handleCopyFolder(item.id, currentFolderId, item.folderName);
                } else {
                    await handleCopyFile(item.id, currentFolderId);
                }
                message.success(`${item.type === 'folder' ? 'Folder' : 'File'} copied successfully`);
            }
        } catch (error) {
            message.error(`Failed to paste: ${error.message}`);
        }
    };

    // Copy folder function
    const handleCopyFolder = async (folderId, targetFolderId, folderName) => {
        try {
            await dispatch(copyFolder({ folderId, targetFolderId, folderName })).unwrap();
            dispatch(getFolderFiles(currentFolderId || 0));
            dispatch(fetchFolderList(userId));
        } catch (error) {
            console.error("Error copying folder:", error);
            message.error(error.message || "Failed to copy folder");
        }
    };

    // Copy file function
    const handleCopyFile = async (fileId, targetFolderId) => {
        try {
            await dispatch(copyFile({ fileId, targetFolderId })).unwrap();
            dispatch(getFolderFiles(currentFolderId || 0));
        } catch (error) {
            console.error("Error copying file:", error);
            message.error(error.message || "Failed to copy file");
        }
    };

    // Clear clipboard
    const handleClearClipboard = () => {
        setClipboard({ item: null, operation: null, sourceFolder: null });
        message.success("Clipboard cleared");
    };

    const handleAddFolder = () => {
        setNewFolderName("");
        setIsModalVisible(true);
    };

    const handleFileClick = (file) => {
        navigate("/admin/file-viewer", { state: { file: file, parentFolderId: currentFolderId } });
    };

    const handleFolderSubmit = async () => {
        if (newFolderName.trim() !== "") {
            try {
                const result = await dispatch(
                    createFolder({ folderName: newFolderName, userId: userId, parentId: currentFolderId || 0 })
                ).unwrap();
                message.success(result.message);
                setNewFolderName("");
                setIsModalVisible(false);
                dispatch(fetchFolderList(userId));
                dispatch(getFolderFiles(currentFolderId || 0));
            } catch (err) {
                message.error("Failed to create folder");
            }
        }
    };

    const handleRenameFolder = async () => {
        if (!renameFolderName.trim() || !selectedFolder) {
            message.error("Please select a folder and provide a valid new name.");
            return;
        }
        try {
            const result = await dispatch(renameFolder({ oldName: selectedFolder, folderId: selectedFolderId, newFolderName: renameFolderName, parentId: currentFolderId })).unwrap();
            message.success(result.message);
            setRenameFolderName("");
            setSelectedFolder(null);
            setIsRenameModalVisible(false);
            dispatch(getFolderFiles(currentFolderId || 0));
            dispatch(fetchFolderList(userId));
        } catch (err) {
            message.error(err.message || "Failed to rename folder");
        }
    };

    const handleRenameClick = (folderId, folderName) => {
        setSelectedFolder(folderName);
        setRenameFolderName(folderName);
        setSelectedFolderId(folderId);
        setIsRenameModalVisible(true);
    };

    const handleDeleteFolder = async (folderId, folderName) => {
        try {
            const result = await dispatch(deleteFolder({ folderId, folderName })).unwrap();
            message.success(result.message);
            setSelectedFolder(null);
            dispatch(fetchFolderList(userId));
        } catch (err) {
            message.error(err.message || "Failed to delete folder");
        }
    };

    const confirmDeleteFolder = (folderId, folderName) => {
        Modal.confirm({
            title: "Are you sure?",
            content: `Do you really want to delete the folder "${folderName}"? This action cannot be undone.`,
            okText: "Yes, Delete",
            okType: "danger",
            cancelText: "Cancel",
            onOk: () => handleDeleteFolder(folderId, folderName),
        });
    };

    const handleUserListModalOpen = (folderId, folderName) => {
        setSelectedFolderId(folderId);
        setSelectedFolderName(folderName);
        setIsUserListModalVisible(true);
    };

    const handleUserListModalClose = () => {
        setIsUserListModalVisible(false);
    };

    // Enhanced kebab menu with cut/copy options
 const kebabMenu = (item) => (
    <Menu>
        <Menu.Item 
            icon={<ScissorOutlined />}
            onClick={() => handleCut(item)}
        >
            Cut
        </Menu.Item>
        <Menu.Item 
            icon={<CopyOutlined />}
            onClick={() => handleCopy(item)}
        >
            Copy
        </Menu.Item>

        {item.type !== 'file' && (
            <>
                <Menu.Divider />
                <Menu.Item onClick={() => handleRenameClick(item.id, item.folderName)}>
                    Rename
                </Menu.Item>

                {item.type === 'folder' && (
                    <Menu.Item onClick={() => handleUserListModalOpen(item.id, item.folderName)}>
                        Assign Permission
                    </Menu.Item>
                )}

                <Menu.Divider />
                <Menu.Item danger onClick={() => confirmDeleteFolder(item.id, item.folderName)}>
                    Delete
                </Menu.Item>
            </>
        )}
    </Menu>
);

    // Context menu for the main area (right-click to paste)
    const contextMenu = (
        <Menu>
            <Menu.Item 
                icon={<SnippetsOutlined />}
                onClick={handlePaste}
                disabled={!clipboard.item}
            >
                Paste {clipboard.item ? `(${clipboard.item.folderName})` : ''}
            </Menu.Item>
            <Menu.Item 
                onClick={handleClearClipboard}
                disabled={!clipboard.item}
            >
                Clear Clipboard
            </Menu.Item>
        </Menu>
    );

    const filteredUsers = userList
        .filter((user) =>
            `${user.firstName} ${user.lastName}`
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => a.firstName.localeCompare(b.firstName));

    const handleEditUserChange = (userId, checked) => {
        setSelectedEditUsers((prevEditUsers) => {
            const updatedEditUsers = checked
                ? [...prevEditUsers, userId]
                : prevEditUsers.filter((id) => id !== userId);

            if (checked) {
                setSelectedViewUsers((prevViewUsers) =>
                    prevViewUsers.includes(userId) ? prevViewUsers : [...prevViewUsers, userId]
                );
            }
            return updatedEditUsers;
        });
    };

    const handleViewUserChange = (userId, checked) => {
        setSelectedViewUsers((prevViewUsers) =>
            checked
                ? [...prevViewUsers, userId]
                : prevViewUsers.filter((id) => id !== userId)
        );

        if (!checked) {
            setSelectedEditUsers((prevEditUsers) =>
                prevEditUsers.filter((id) => id !== userId)
            );
        }
    };

    const filteredFolders = currentFolderId
        ? folders.filter(folder => folder.parentFolderId === currentFolderId)
        : folders.filter(folder => !folder.parentFolderId);

    const handleOpenFolder = async (folderId) => {
        try {
            dispatch(setSliceCurrentFolderId(folderId));
            setCurrentFolderId(folderId);
            await dispatch(getFolderFiles(folderId)).unwrap();
        } catch (error) {
            console.error('Error opening folder:', error);
            message.error('Failed to open folder');
        }
    };


    const uploadProps = {
        customRequest: async ({ file }) => {
            handleFileUpload(file);
        },
        showUploadList: false,
    };

    const handleFileUpload = async (file) => {
        try {
            const result = await dispatch(uploadFile({ file: file, folderId: currentFolderId, uploadedBy: userId })).unwrap();
            message.success(result.message);
            dispatch(getFolderFiles(currentFolderId || 0))
        } catch (err) {
            message.error("File upload failed");
        }
    };

    const allowedFolderIds = new Set(folders.map(folder => folder.id));
    const combinedData = [
        ...folderContent.folders
            .filter(folder => folder.userId.includes(userId))
            .map((folder) => ({
                id: folder.id,
                folderName: folder.folderName,
                editFolderUserId: folder.editFolderUserId,
                type: 'folder',
                children: folder.children || [],
            })),
        ...folderContent.files.map((file) => ({
            id: file.id,
            folderName: file.name,
            type: 'file',
            uploadedBy: file.uploadedBy,
            createdAt: file.createdAt,
        })),
    ].sort((a, b) => a.folderName.localeCompare(b.folderName, undefined, { sensitivity: 'base' }));

    const filteredCombinedData = combinedData.filter((item) => {
        if (!folderSearchQuery?.trim()) return true;
        return String(item.folderName || "")
            .toLowerCase()
            .includes(folderSearchQuery.trim().toLowerCase());
    });

    const handleDragStart = (e, item) => {
        setDraggedItem(item);
        setIsDragging(true);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', JSON.stringify(item));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnter = (e, item) => {
        e.preventDefault();
        if (item.type === 'folder') {
            setDragOverItem(item);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOverItem(null);
    };

    const handleDrop = async (e, targetFolder) => {
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(false);
        setDragOverItem(null);

        if (!draggedItem) return;

        if (draggedItem.id === targetFolder.id) {
            message.warning("Cannot move item to itself");
            return;
        }

        if (draggedItem.type === 'folder' && targetFolder.type === 'folder') {
            if (await isChildFolder(draggedItem.id, targetFolder.id)) {
                message.warning("Cannot move folder into its own subfolder");
                return;
            }
        }

        try {
            if (draggedItem.type === 'folder') {
                await handleMoveFolder(draggedItem.id, targetFolder.id, draggedItem.folderName);
            } else if (draggedItem.type === 'file') {
                await handleMoveFile(draggedItem.id, targetFolder.id);
            }
        } catch (error) {
            console.error('Error moving item:', error);
            message.error('Failed to move item');
        }

        setDraggedItem(null);
    };

    const handleExternalFileDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const files = Array.from(e.dataTransfer.files);
        if (files.length === 0) return;

        for (const file of files) {
            try {
                const result = await dispatch(uploadFile({ 
                    file: file, 
                    folderId: currentFolderId, 
                    uploadedBy: userId 
                })).unwrap();
                message.success(`${file.name} uploaded successfully`);
            } catch (err) {
                message.error(`Failed to upload ${file.name}`);
            }
        }

        dispatch(getFolderFiles(currentFolderId || 0));
    };

    const handleMoveFolder = async (folderId, newParentId, folderName) => {
        await dispatch(moveFolder({ folderId, newParentId, folderName }));
        dispatch(getFolderFiles(currentFolderId || 0));
        dispatch(fetchFolderList(userId));
    };

    const handleMoveFile = async (fileId, newFolderId) => {
        await dispatch(moveFile({ fileId, newFolderId }));
        dispatch(getFolderFiles(currentFolderId || 0));
    };

    const isChildFolder = async (sourceFolderId, targetFolderId) => {
        const checkRecursively = (folderId, parentId) => {
            const folder = folderContent.folders.find(f => f.id === folderId);
            if (!folder) return false;
            if (folder.parentId === parentId) return true;
            if (folder.parentId === 0) return false;
            return checkRecursively(folder.parentId, parentId);
        };

        return checkRecursively(targetFolderId, sourceFolderId);
    };

    const columns = [
        {
            title: "Folder Name",
            dataIndex: "folderName",
            key: "folderName",
            render: (text, record) => (
                <div
                    draggable
                    onDragStart={(e) => handleDragStart(e, record)}
                    onDragOver={handleDragOver}
                    onDragEnter={(e) => handleDragEnter(e, record)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => record.type === 'folder' ? handleDrop(e, record) : null}
                    style={{
                        cursor: record.type === 'folder' ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        borderRadius: '4px',
                        backgroundColor: dragOverItem?.id === record.id ? '#e6f7ff' : 
                                        (clipboard.item?.id === record.id ? '#fff1f0' : 'transparent'),
                        border: dragOverItem?.id === record.id ? '2px dashed #1890ff' : 
                               (clipboard.item?.id === record.id && clipboard.operation === 'cut' ? '2px dashed #ff4d4f' : '2px solid transparent'),
                        opacity: draggedItem?.id === record.id || 
                                (clipboard.item?.id === record.id && clipboard.operation === 'cut') ? 0.5 : 1,
                        transition: 'all 0.2s ease'
                    }}
                    onClick={() => {
                        if (record.type === 'folder') {
                            handleOpenFolder(record.id);
                        } else if (record.type === 'file') {
                            handleFileClick(record);
                        }
                    }}
                >
                    {record.type === 'folder' ? (
                        <FolderOutlined
                            style={{
                                marginRight: 8,
                                color: '#1890ff'
                            }}
                        />
                    ) : (
                        <FileOutlined
                            style={{
                                marginRight: 8,
                                color: '#8c8c8c'
                            }}
                        />
                    )}
                    <span style={{
                        fontWeight: 500,
                        color: record.type === 'folder' ? '#1890ff' : '#000'
                    }}>
                        {text}
                    </span>
                    {clipboard.item?.id === record.id && (
                        <span style={{
                            marginLeft: 8,
                            fontSize: '12px',
                            color: clipboard.operation === 'cut' ? '#ff4d4f' : '#52c41a',
                            fontWeight: 'bold'
                        }}>
                            [{clipboard.operation.toUpperCase()}]
                        </span>
                    )}
                </div>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                record.editFolderUserId?.includes(userId) || record.type === 'file' ? (
                    <Row>
                        <Col>
                            <Dropdown overlay={kebabMenu(record)} trigger={["click"]}>
                                <Button icon={<MoreOutlined />} />
                            </Dropdown>
                        </Col>
                    </Row>
                ) : null
            ),
        },
    ];

    return (
        <Fragment>
            <Card
                title={
                    <Row justify="space-between" align="middle">
                        <Title level={4} style={{ margin: 0 }}>
                            Folder Management
                        </Title>

                        <Row gutter={8}>
                            <Col>
                                <Input
                                    allowClear
                                    placeholder="Filter folders/files..."
                                    value={folderSearchQuery}
                                    onChange={(e) => setFolderSearchQuery(e.target.value)}
                                    style={{ width: 280 }}
                                />
                            </Col>
                            {clipboard.item && (
                                <Col>
                                    <Button
                                        icon={<SnippetsOutlined />}
                                        onClick={handlePaste}
                                        style={{
                                            backgroundColor: clipboard.operation === 'cut' ? '#fff1f0' : '#f6ffed',
                                            borderColor: clipboard.operation === 'cut' ? '#ff4d4f' : '#52c41a'
                                        }}
                                    >
                                        Paste ({clipboard.item.folderName})
                                    </Button>
                                </Col>
                            )}
                            
                            {currentFolderId !== 0 ? (
                                <Col>
                                    <Upload {...uploadProps}>
                                        <Button type="default" icon={<UploadOutlined />}>
                                            Upload File
                                        </Button>
                                    </Upload>
                                </Col>
                            ) : (
                                <Col></Col>
                            )}
                            
                            <Col>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleAddFolder}
                                >
                                    Create Folder
                                </Button>
                            </Col>
                        </Row>
                    </Row>
                }
                bordered={false}
                style={{
                    width: '100%',
                    maxWidth: 1200,
                    margin: '0 auto'
                }}
            >
                {/* Breadcrumb Navigation */}
                <div style={{ marginBottom: 2, padding: '2px 0' }}>
                    <Breadcrumb
                        separator="/"
                        style={{
                            fontSize: '14px',
                            color: '#8c8c8c'
                        }}
                    >
                        {breadcrumbItems.map((item, index) => (
                            <Breadcrumb.Item key={item.id}>
                                {index === breadcrumbItems.length - 1 ? (
                                    <span style={{ color: '#8c8c8c' }}>{item.title}</span>
                                ) : (
                                    <span
                                        onClick={() => handleBreadcrumbClick(item.id)}
                                        style={{
                                            color: '#8c8c8c',
                                            cursor: 'pointer',
                                            transition: 'color 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.color = '#1890ff';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.color = '#8c8c8c';
                                        }}
                                    >
                                        {item.title}
                                    </span>
                                )}
                            </Breadcrumb.Item>
                        ))}
                    </Breadcrumb>
                </div>

                {/* Drop zone with context menu */}
                <Dropdown overlay={contextMenu} trigger={['contextMenu']}>
                    <div
                        ref={dropZoneRef}
                        onDrop={handleExternalFileDrop}
                        onDragOver={handleDragOver}
                        style={{
                            minHeight: '200px',
                            border: isDragging ? '2px dashed #1890ff' : '2px solid transparent',
                            borderRadius: '8px',
                            backgroundColor: isDragging ? '#f0f9ff' : 'transparent',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <Spin spinning={documentLoading}>
                            <Table
                                loading={documentLoading}
                                dataSource={filteredCombinedData}
                                columns={columns}
                                pagination={{
                                    showSizeChanger: true,
                                }}
                                expandable={{
                                    showExpandColumn: false
                                }}
                            />
                        </Spin>
                    </div>
                </Dropdown>

                {/* Visual feedback for operations */}
                {isDragging && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        pointerEvents: 'none',
                        zIndex: 9999
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'white',
                            padding: '10px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            fontSize: '16px',
                            fontWeight: 'bold'
                        }}>
                            Drop to move item or upload files
                        </div>
                    </div>
                )}

                {/* Clipboard status indicator */}
                {clipboard.item && (
                    <div style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '20px',
                        backgroundColor: clipboard.operation === 'cut' ? '#fff1f0' : '#f6ffed',
                        border: `1px solid ${clipboard.operation === 'cut' ? '#ff4d4f' : '#52c41a'}`,
                        borderRadius: '8px',
                        padding: '12px 16px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 1000
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {clipboard.operation === 'cut' ? <ScissorOutlined /> : <CopyOutlined />}
                            <span style={{ fontSize: '14px' }}>
                                {clipboard.operation === 'cut' ? 'Cut' : 'Copied'}: {clipboard.item.folderName}
                            </span>
                            <Button 
                                size="small" 
                                type="text" 
                                onClick={handleClearClipboard}
                                style={{ marginLeft: '8px' }}
                            >
                                ×
                            </Button>
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            Right-click for paste options
                        </div>
                    </div>
                )}
            </Card>

            {/* Existing modals remain the same */}
            <Modal
                title="Create New Folder"
                visible={isModalVisible}
                onOk={handleFolderSubmit}
                onCancel={() => setIsModalVisible(false)}
                okText="Create"
                cancelText="Cancel"
            >
                <Input
                    placeholder="Enter folder name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                />
            </Modal>

            <Modal
                title="Rename Folder"
                visible={isRenameModalVisible}
                onOk={handleRenameFolder}
                onCancel={() => setIsRenameModalVisible(false)}
                okText="Rename"
                cancelText="Cancel"
            >
                <Input
                    placeholder="Enter new folder name"
                    value={renameFolderName}
                    onChange={(e) => setRenameFolderName(e.target.value)}
                />
            </Modal>

            <Modal
                title="Employee List"
                visible={isUserListModalVisible}
                onCancel={handleUserListModalClose}
                footer={[
                    <Button key="cancel" onClick={handleUserListModalClose}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={async () => {
                            if (selectedFolderId) {
                                try {
                                    const response = await dispatch(assignFolderPermission({
                                        folderId: selectedFolderId,
                                        editFolderUserId: selectedEditUsers,
                                        userId: selectedViewUsers
                                    }));

                                    if (response.success) {
                                        dispatch(getFolderFiles(cfid || 0))
                                        handleUserListModalClose();
                                    } else {
                                        dispatch(getFolderFiles(cfid || 0))
                                        handleUserListModalClose();
                                    }
                                } catch (error) {
                                    message.error("An error occurred while assigning permissions.");
                                }
                            } else {
                                message.error("No folder selected.");
                            }
                        }}
                    >
                        Save
                    </Button>,
                ]}
                bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
                <Input
                    placeholder="Search Employee"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ marginBottom: 16 }}
                />

                {filteredUsers
                    .slice()
                    .sort((a, b) => a.firstName.localeCompare(b.firstName))
                    .map((user) => (
                        <Row key={user.id} gutter={16} align="middle" style={{ marginBottom: 8 }}>
                            <Col flex="auto">{`${user.firstName} ${user.lastName}`}</Col>
                            <Col>
                                <Checkbox
                                    checked={selectedViewUsers.includes(user.id)}
                                    onChange={(e) => handleViewUserChange(user.id, e.target.checked)}
                                    disabled={folderContent?.folders
                                        ?.find(folder => folder.id === selectedFolderId)?.createdBy === user.id}
                                >
                                    View
                                </Checkbox>
                            </Col>
                            <Col>
                                <Checkbox
                                    checked={selectedEditUsers.includes(user.id)}
                                    onChange={(e) => handleEditUserChange(user.id, e.target.checked)}
                                    disabled={folderContent?.folders
                                        ?.find(folder => folder.id === selectedFolderId)?.createdBy === user.id}
                                >
                                    Edit
                                </Checkbox>
                            </Col>
                        </Row>
                    ))}
            </Modal>
        </Fragment>
    );
};

export default FolderManager;

